import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import posthog from 'posthog-js';
import { captureError } from './utils/analytics';
import { register as registerServiceWorker } from './serviceWorkerRegistration';
import { initNativeShell } from './native/initNativeShell';

// Chunk-load recovery lives in utils/lazyRetry.js (retry with backoff →
// one guarded reload → error boundary). The vite:preloadError listener that
// used to sit here reloaded on the FIRST failure, which skipped the graceful
// in-place retry and spent the 30s reload budget before it could matter
// (root cause of the 2026-08-26 dead-Settings session). Every route/shell
// dynamic import is now wrapped in lazyWithRetry, and feature-lib imports
// (jspdf) use retryImport, so the unhandled preload error simply propagates
// into those handlers.

// Render first — defer PostHog init so it never blocks FCP.
// posthog-js queues calls made before init(), so track.* and error listeners
// below work immediately; events flush once init completes on the next tick.
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

setTimeout(() => {
  posthog.init(import.meta.env.REACT_APP_POSTHOG_KEY, {
    api_host: import.meta.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
    // ui_host must point at the real PostHog domain when api_host is a reverse
    // proxy (g.heyward.ai) — otherwise toolbar + session-replay links break.
    ui_host: 'https://us.posthog.com',
    // 'history_change' (not `true`): `true` only captures the initial
    // document load, so every in-app route change in this SPA was invisible
    // — /settings, /deals/:id, /strategy never showed up as pageviews
    // (found 2026-08-19 while diagnosing the empty paywall funnel).
    // 'history_change' keeps the initial pageview AND fires one $pageview
    // per pushState/replaceState/popstate whose pathname actually changed
    // (same-pathname replaceState, e.g. query-param cleanup, is ignored by
    // the SDK's last-pathname guard).
    capture_pageview: 'history_change',
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
    },
    loaded: (ph) => {
      if (import.meta.env.REACT_APP_ENV !== 'production') {
        ph.opt_out_capturing();
      }
    },
  });
}, 0);

// Capture uncaught errors and unhandled promise rejections into PostHog's
// Error Tracking via the stable captureException API. Non-production sessions
// are opted out in posthog.init's loaded() hook above, so these no-op in dev.
window.addEventListener('error', (event) => {
  if (event.error instanceof Error) {
    captureError(event.error, { mechanism: 'window.onerror' });
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  if (reason instanceof Error) {
    captureError(reason, { mechanism: 'unhandledrejection' });
  } else if (reason != null) {
    captureError(new Error(String(reason)), {
      mechanism: 'unhandledrejection',
      non_error_rejection: true,
    });
  }
});

// PWA: production-only; no-ops (and cleans up stale workers) in dev.
registerServiceWorker();

// Capacitor shell (status bar, Android back button, safe areas): no-op on web.
initNativeShell();
